import { Check, Copy, ExternalLink, Loader2, Pencil, Plus, Trash2, Wand2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { EmptyState, ErrorBanner } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import type {
  CreateExternalMcpRequest,
  ExternalMcpListResponse,
  ExternalMcpSummary,
  ExternalMcpTransport,
  KbView,
  UpdateExternalMcpRequest,
} from "@/lib/types";
import { cn } from "@/lib/utils";

interface KbListResponse {
  // The gateway returns a bare array; some pages also accept `{ items }` wrappers.
  items?: KbView[];
}

/**
 * Resolve the MCP base URL from `VITE_MCP_BASE_URL` (set per
 * deployment) with a sensible local-dev fallback. The MCP endpoint is
 * served by `knowledge-service` directly, NOT via the gateway, so we
 * can't just stitch `window.location.origin + "/mcp/..."`.
 *
 * Empty string ⇒ render the relative path with a "configure base URL"
 * hint so the user can still copy + paste the suffix into their
 * client config and prepend their own host.
 */
function mcpBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_MCP_BASE_URL;
  if (fromEnv && fromEnv.length > 0) return fromEnv.replace(/\/$/, "");
  return "";
}

interface ExposedKb {
  kb: KbView;
  endpoint: string;
  toolsLabel: "read" | "read-write";
}

interface McpBrowserProps {
  initialTeamSlug: string;
  initialKbs: KbView[];
  initialExternalServers: ExternalMcpSummary[];
  initialExternalServersError: string | null;
  /** When false, external-MCP mutation actions are hidden. The backend
   *  still gates by role; this just keeps the surface honest. */
  isAdmin: boolean;
}

/**
 * `/mcp` content. Two stacked cards:
 *
 *   1. "MCP servers we expose" — every KB in this team that has
 *      `mcp_exposed=true`. We surface the JSON-RPC endpoint URL with
 *      a copy-button so users can drop it straight into Claude
 *      Desktop / Cursor.
 *
 *   2. "External MCP servers" — registered remote tool surfaces the
 *      runtime hands tool calls off to. Admin-only CRUD; auth headers
 *      are encrypted server-side and never returned over the wire.
 */
export function McpBrowser({
  initialTeamSlug,
  initialKbs,
  initialExternalServers,
  initialExternalServersError,
  isAdmin,
}: McpBrowserProps) {
  const t = useT();
  const [kbs, setKbs] = useState<KbView[]>(initialKbs);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const teamSlug = initialTeamSlug;
  const base = mcpBaseUrl();
  const showHostHint = base === "";

  const exposed: ExposedKb[] = useMemo(() => {
    return kbs
      .filter((kb) => kb.mcp_exposed)
      .map((kb) => {
        // Personal KBs are addressable as both `kb.<slug>` and `kb.me`;
        // the JSON-RPC URL accepts either form. We prefer `me` because
        // it's stable across renames and matches the docs.
        const slug = kb.scope === "personal" ? "me" : kb.slug;
        const path = `/mcp/${teamSlug}/${slug}`;
        return {
          kb,
          endpoint: base ? `${base}${path}` : path,
          toolsLabel: kb.mcp_writable ? "read-write" : "read",
        };
      });
  }, [kbs, teamSlug, base]);

  // Refetch on demand — useful after the user toggles `mcp_exposed`
  // on a KB in the Knowledge page and bounces back here. Wrapped in
  // `useCallback` so the visibility-change effect can keep a stable
  // reference and pass biome's exhaustive-deps lint.
  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await api<KbView[] | KbListResponse>("/kbs");
      const next = Array.isArray(res) ? res : (res.items ?? []);
      setKbs(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-pull when the page is brought back into focus — cheap signal
  // that the user toggled an MCP flag elsewhere.
  useEffect(() => {
    const onVis = (): void => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refresh]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div className="space-y-1">
            <CardTitle className="text-base">{t("mcp.section.exposed")}</CardTitle>
            <CardDescription>{t("mcp.section.exposed.desc")}</CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void refresh()}
            disabled={loading}
            className="h-7 px-2 text-[11px]"
          >
            {loading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            {t("common.refresh")}
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {error ? <ErrorBanner>{error}</ErrorBanner> : null}
          {showHostHint ? (
            <div className="rounded-md border border-border/70 bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground">
              <code className="font-mono">NEXT_PUBLIC_MCP_BASE_URL</code> is not set. Endpoints
              below show only the path — prepend your own knowledge-service host.
            </div>
          ) : null}
          {exposed.length === 0 ? (
            <EmptyState>
              <p className="mb-3">{t("mcp.empty.exposed")}</p>
              <Button variant="default" size="sm" asChild>
                <Link to="/knowledge">
                  <Wand2 className="mr-1.5 h-3.5 w-3.5" />
                  {t("mcp.empty.exposed.cta")}
                </Link>
              </Button>
            </EmptyState>
          ) : (
            <ul className="space-y-2">
              {exposed.map((entry) => (
                <ExposedKbRow key={entry.kb.id} entry={entry} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <ExternalMcpSection
        initialServers={initialExternalServers}
        initialError={initialExternalServersError}
        isAdmin={isAdmin}
      />
    </div>
  );
}

function ExposedKbRow({ entry }: { entry: ExposedKb }) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(entry.endpoint);
      setCopied(true);
      toast.success(t("common.copied"));
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(`Could not copy. URL: ${entry.endpoint}`);
    }
  };

  const scopeLabel = entry.kb.scope === "personal" ? t("mcp.scope.user") : t("mcp.scope.team");

  return (
    <li className="rounded-md border border-border/70 bg-card/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-semibold text-foreground">{entry.kb.name}</span>
        <Badge variant="outline" className="h-4 px-1.5 text-[9px] uppercase tracking-[0.1em]">
          {scopeLabel}
        </Badge>
        <Badge
          variant={entry.toolsLabel === "read-write" ? "lemon" : "secondary"}
          className="h-4 px-1.5 text-[9px] uppercase tracking-[0.1em]"
        >
          {entry.toolsLabel === "read-write" ? "read · write" : "read-only"}
        </Badge>
      </div>
      {entry.kb.description ? (
        <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
          {entry.kb.description}
        </p>
      ) : null}
      <div className="mt-2 flex items-center gap-2">
        <code
          className={cn(
            "min-w-0 flex-1 truncate rounded-md border border-border/70 bg-background px-2 py-1.5 font-mono text-[11px]",
          )}
          title={entry.endpoint}
        >
          {entry.endpoint}
        </code>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void onCopy()}
          className="h-7 shrink-0 px-2 text-[11px]"
        >
          {copied ? (
            <>
              <Check className="mr-1 h-3 w-3" />
              {t("common.copied")}
            </>
          ) : (
            <>
              <Copy className="mr-1 h-3 w-3" />
              {t("common.copy")}
            </>
          )}
        </Button>
        {entry.endpoint.startsWith("http") ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            asChild
            className="h-7 shrink-0 px-2 text-[11px]"
          >
            <a href={`${entry.endpoint}/_status`} target="_blank" rel="noreferrer">
              <ExternalLink className="h-3 w-3" />
            </a>
          </Button>
        ) : null}
      </div>
    </li>
  );
}

// ────────────────────────────────────────────────────────────────────
// External MCP servers — admin-gated CRUD.
// ────────────────────────────────────────────────────────────────────

function ExternalMcpSection({
  initialServers,
  initialError,
  isAdmin,
}: {
  initialServers: ExternalMcpSummary[];
  initialError: string | null;
  isAdmin: boolean;
}) {
  const t = useT();
  const [servers, setServers] = useState<ExternalMcpSummary[]>(initialServers);
  const [error, setError] = useState<string | null>(initialError);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ExternalMcpSummary | null>(null);

  async function refresh() {
    try {
      const res = await api<ExternalMcpListResponse>("/mcp/external");
      setServers(res.items);
      setError(null);
    } catch (err) {
      setError(messageOf(err, "Failed to load external MCP servers"));
    }
  }

  async function handleDelete(server: ExternalMcpSummary) {
    if (!confirm(t("mcp.external.deleteConfirm", { slug: server.slug }))) {
      return;
    }
    try {
      await api(`/mcp/external/${server.id}`, { method: "DELETE" });
      toast.success(t("mcp.external.toast.deleted", { slug: server.slug }));
      await refresh();
    } catch (err) {
      toast.error(messageOf(err, "Failed to delete external MCP server"));
    }
  }

  function onAddClick() {
    if (!isAdmin) {
      toast.info(t("mcp.external.error.adminOnly"));
      return;
    }
    setCreateOpen(true);
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div className="space-y-1">
          <CardTitle className="text-base">{t("mcp.section.external")}</CardTitle>
          <CardDescription>{t("mcp.section.external.desc")}</CardDescription>
        </div>
        <Button
          size="sm"
          variant={isAdmin ? "default" : "outline"}
          onClick={onAddClick}
          disabled={!isAdmin}
          className="h-7 px-2 text-[11px]"
          title={isAdmin ? undefined : t("mcp.external.error.adminOnly")}
        >
          <Plus className="mr-1 h-3 w-3" />
          {t("mcp.external.cta")}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? <ErrorBanner>{error}</ErrorBanner> : null}
        {servers.length === 0 ? (
          <EmptyState>{t("mcp.external.empty")}</EmptyState>
        ) : (
          <ExternalMcpTable
            servers={servers}
            isAdmin={isAdmin}
            onEdit={setEditing}
            onDelete={handleDelete}
          />
        )}
      </CardContent>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        {createOpen ? (
          <ExternalMcpFormDialog
            mode="create"
            onSubmitted={() => {
              setCreateOpen(false);
              void refresh();
            }}
          />
        ) : null}
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(open) => (open ? null : setEditing(null))}>
        {editing ? (
          <ExternalMcpFormDialog
            mode="edit"
            server={editing}
            onSubmitted={() => {
              setEditing(null);
              void refresh();
            }}
          />
        ) : null}
      </Dialog>
    </Card>
  );
}

function ExternalMcpTable({
  servers,
  isAdmin,
  onEdit,
  onDelete,
}: {
  servers: ExternalMcpSummary[];
  isAdmin: boolean;
  onEdit: (server: ExternalMcpSummary) => void;
  onDelete: (server: ExternalMcpSummary) => void;
}) {
  const t = useT();
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("mcp.external.col.name")}</TableHead>
          <TableHead>{t("mcp.external.col.transport")}</TableHead>
          <TableHead>{t("mcp.external.col.url")}</TableHead>
          <TableHead>{t("mcp.external.col.credentials")}</TableHead>
          <TableHead>{t("mcp.external.col.status")}</TableHead>
          {isAdmin ? <TableHead aria-label="actions" /> : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {servers.map((server) => (
          <TableRow key={server.id}>
            <TableCell className="font-mono text-xs">
              {server.slug}
              <div className="text-[11px] text-muted-foreground">{server.display_name}</div>
              {server.description ? (
                <div className="mt-0.5 text-[11px] text-muted-foreground/80">
                  {server.description}
                </div>
              ) : null}
            </TableCell>
            <TableCell>
              <Badge variant="outline">{server.transport}</Badge>
            </TableCell>
            <TableCell className="max-w-[24rem] truncate font-mono text-[11px]" title={server.url}>
              {server.url}
            </TableCell>
            <TableCell>
              {server.has_credentials ? (
                <Badge variant="ok">{t("channels.creds.in")}</Badge>
              ) : (
                <Badge variant="outline">{t("channels.creds.none")}</Badge>
              )}
            </TableCell>
            <TableCell>
              {server.enabled ? (
                <Badge variant="ok">{t("channels.action.enable")}</Badge>
              ) : (
                <Badge variant="warn">{t("channels.action.disable")}</Badge>
              )}
            </TableCell>
            {isAdmin ? (
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`${t("channels.action.edit")} ${server.slug}`}
                  onClick={() => onEdit(server)}
                >
                  <Pencil />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`${t("channels.action.delete")} ${server.slug}`}
                  onClick={() => onDelete(server)}
                >
                  <Trash2 className="text-destructive" />
                </Button>
              </TableCell>
            ) : null}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

interface ExternalMcpFormProps {
  mode: "create" | "edit";
  /** Edit mode requires this; create mode ignores it. */
  server?: ExternalMcpSummary;
  onSubmitted: () => void;
}

function ExternalMcpFormDialog({ mode, server, onSubmitted }: ExternalMcpFormProps) {
  const t = useT();

  const [slug, setSlug] = useState(server?.slug ?? "");
  const [displayName, setDisplayName] = useState(server?.display_name ?? "");
  const [description, setDescription] = useState(server?.description ?? "");
  const [transport, setTransport] = useState<ExternalMcpTransport>(
    (server?.transport as ExternalMcpTransport) ?? "http",
  );
  const [url, setUrl] = useState(server?.url ?? "");
  const [headersText, setHeadersText] = useState<string>("");
  const [dropHeaders, setDropHeaders] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const title = mode === "create" ? t("mcp.external.dialog.create") : t("mcp.external.dialog.edit");
  const submitLabel = mode === "create" ? t("channels.action.create") : t("common.save");

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (submitting) return;

    const trimmedSlug = slug.trim();
    if (!trimmedSlug) {
      toast.error(`${t("mcp.external.field.slug")}: required`);
      return;
    }
    const trimmedDisplay = displayName.trim();
    if (!trimmedDisplay) {
      toast.error(`${t("mcp.external.field.displayName")}: required`);
      return;
    }
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      toast.error(`${t("mcp.external.field.url")}: required`);
      return;
    }

    let parsedHeaders: Record<string, string> | null | undefined;
    if (mode === "edit" && dropHeaders) {
      parsedHeaders = null;
    } else if (headersText.trim()) {
      try {
        const obj = JSON.parse(headersText) as Record<string, unknown>;
        if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
          throw new Error("must be a JSON object");
        }
        const out: Record<string, string> = {};
        for (const [k, v] of Object.entries(obj)) {
          if (typeof v !== "string") {
            throw new Error(`header "${k}" must be a string`);
          }
          out[k] = v;
        }
        parsedHeaders = out;
      } catch (err) {
        toast.error(`${t("mcp.external.field.headers")}: ${messageOf(err, "invalid JSON")}`);
        return;
      }
    } else if (mode === "create") {
      parsedHeaders = null;
    }
    // mode === "edit" && no input + no drop → leave field unset → keep existing.

    setSubmitting(true);
    try {
      if (mode === "create") {
        const body: CreateExternalMcpRequest = {
          slug: trimmedSlug,
          display_name: trimmedDisplay,
          description: description.trim() ? description.trim() : null,
          transport,
          url: trimmedUrl,
          auth_headers: parsedHeaders ?? null,
          enabled: true,
        };
        await api("/mcp/external", { method: "POST", body: JSON.stringify(body) });
        toast.success(t("mcp.external.toast.created", { slug: trimmedSlug }));
      } else {
        if (!server) {
          toast.error("Edit dialog opened without a server");
          return;
        }
        const body: UpdateExternalMcpRequest = {};
        if (trimmedDisplay !== server.display_name) {
          body.display_name = trimmedDisplay;
        }
        const newDesc = description.trim() ? description.trim() : null;
        if (newDesc !== (server.description ?? null)) {
          body.description = newDesc;
        }
        if (transport !== server.transport) {
          body.transport = transport;
        }
        if (trimmedUrl !== server.url) {
          body.url = trimmedUrl;
        }
        if (parsedHeaders !== undefined) {
          body.auth_headers = parsedHeaders;
        }
        if (Object.keys(body).length === 0) {
          toast.message("Nothing changed");
          setSubmitting(false);
          return;
        }
        await api(`/mcp/external/${server.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        toast.success(t("mcp.external.toast.updated", { slug: server.slug }));
      }
      onSubmitted();
    } catch (err) {
      toast.error(messageOf(err, "Failed to save external MCP server"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{t("mcp.section.external.desc")}</DialogDescription>
      </DialogHeader>
      <form className="grid gap-4" onSubmit={onSubmit}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="ext-mcp-slug">{t("mcp.external.field.slug")}</Label>
            <Input
              id="ext-mcp-slug"
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              placeholder="filesystem-prod"
              maxLength={64}
              disabled={mode === "edit"}
              required
            />
            <p className="text-xs text-muted-foreground">{t("mcp.external.field.slug.hint")}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ext-mcp-transport">{t("mcp.external.field.transport")}</Label>
            <Select
              value={transport}
              onValueChange={(value) => setTransport(value as ExternalMcpTransport)}
            >
              <SelectTrigger id="ext-mcp-transport">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="http">http</SelectItem>
                <SelectItem value="sse">sse</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ext-mcp-display-name">{t("mcp.external.field.displayName")}</Label>
          <Input
            id="ext-mcp-display-name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            maxLength={120}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ext-mcp-description">{t("mcp.external.field.description")}</Label>
          <Input
            id="ext-mcp-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={500}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ext-mcp-url">{t("mcp.external.field.url")}</Label>
          <Input
            id="ext-mcp-url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://mcp.example.com/jsonrpc"
            type="url"
            required
          />
          <p className="text-xs text-muted-foreground">{t("mcp.external.field.url.hint")}</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ext-mcp-headers">{t("mcp.external.field.headers")}</Label>
          <Textarea
            id="ext-mcp-headers"
            value={headersText}
            onChange={(event) => setHeadersText(event.target.value)}
            rows={4}
            spellCheck={false}
            className="font-mono text-xs"
            placeholder='{"Authorization": "Bearer …"}'
            disabled={dropHeaders}
          />
          {mode === "edit" && server?.has_credentials ? (
            <label className="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={dropHeaders}
                onChange={(event) => setDropHeaders(event.target.checked)}
              />
              {t("mcp.external.field.headers.drop")}
            </label>
          ) : null}
          <p className="text-xs text-muted-foreground">
            {mode === "edit" && server?.has_credentials && !headersText.trim() && !dropHeaders
              ? t("mcp.external.field.headers.kept")
              : t("mcp.external.field.headers.hint")}
          </p>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t("common.cancel")}
            </Button>
          </DialogClose>
          <Button type="submit" disabled={submitting}>
            {submitting ? <Loader2 className="animate-spin" /> : null}
            {submitLabel}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}
